/**
 * Contenuti di un nodo: bozze e pubblicazioni con i loro documenti, e il
 * comando che li rende pubblici.
 *
 * Sta qui e non dentro una pagina perche' lo montano **due** superfici, e ci
 * deve stare la stessa cosa: l'alberatura, dove il super admin vede l'albero
 * intero, e «Documenti e cartelle», dove il redattore carica. Se la
 * pubblicazione vivesse in una sola delle due, chi carica non potrebbe
 * pubblicare — che e' esattamente il difetto che questo modulo corregge: il
 * comando stava nella pagina dell'alberatura, riservata al super admin, mentre
 * l'API consente a un redattore di pubblicare dentro il proprio perimetro
 * (`pubblicazione_nel_perimetro` guarda ente e nodi, non il ruolo).
 *
 * E' il caso opposto a quello di `AlberoNodi`, che resta deliberatamente
 * distinto dall'albero della pagina «Alberatura»: la' le due righe portano
 * comandi diversi, qui il blocco e' lo stesso dato con lo stesso comando.
 *
 * QUANDO SI PUO' PUBBLICARE
 * -------------------------
 * Non basta `stato === "bozza"`. Dal secondo caricamento sullo stesso obbligo
 * l'API riapre la pubblicazione esistente — `bozza_per_caricamento_diretto`
 * chiama `apri_versione_di_lavoro` e restituisce la pubblicazione con `stato`
 * ancora `pubblicato` — e nulla riporta mai lo stato a `bozza`. Guardare il solo
 * stato lasciava ogni revisione impubblicabile per sempre, cioe' il caso
 * normale di un obbligo che si aggiorna.
 *
 * Il segnale e' `versione_di_lavoro_aperta`, che l'API dichiara per questo. Non
 * si ricava dai `versioni`: quelli sono le sole versioni **pubblicate**.
 *
 * Il comando non compare mai su una pubblicazione senza versione di lavoro, e
 * non e' una cautela estetica: `pubblica()` in quel caso prende come sorgente
 * l'ultima versione pubblicata e creerebbe una versione nuova identica alla
 * precedente.
 */

import type { ReactNode } from "react";
import type { Documento, Pubblicazione } from "../api/types";
import { daCorreggere, statoDi } from "../lib/accessibilita";
import { daPubblicareInTutti, testoCopie } from "../lib/copie";
import {
  documentoPubblicabile,
  inAttesaDiPubblicazione,
  motivoNonPubblicabile,
  validazioneNonConclusa,
} from "../lib/documenti";
import { Bottone, Chip, ChipAccessibilita, ChipStato } from "./primitive";
import { ETICHETTA_ORIGINE } from "../lib/etichette";

/**
 * Conteggi dei contenuti sulla riga di un nodo.
 *
 * Tre etichette e non una: «pubblicato», «in bozza» e «in revisione» sono
 * stati diversi agli occhi del cittadino — il primo e' sul portale, gli altri
 * due non ancora — e sommarli in un «3 documenti» direbbe all'operatore una
 * cosa falsa su che cosa il Comune ha messo online.
 *
 * «In revisione» esiste perche' `documenti` arriva da `documenti_correnti()`:
 * su una pubblicazione con una versione di lavoro aperta sono i file **in
 * lavorazione**, non quelli pubblici. Contarli come «pubblicati» era il modo
 * piu' diretto di far credere pubblicato un documento che non lo e'.
 *
 * Si contano i **documenti**, non le pubblicazioni: e' il numero che
 * l'operatore confronta con la pagina pubblica.
 */
export function RiepilogoContenuti({
  pubblicazioni,
}: {
  pubblicazioni: readonly Pubblicazione[];
}) {
  if (pubblicazioni.length === 0) return null;

  // Gli archiviati non si contano: sono usciti dalla pubblicazione corrente, e
  // «3 pubblicati» deve dire che cosa il cittadino trova oggi sul portale.
  const documentiDi = (voci: readonly Pubblicazione[]) =>
    voci.reduce(
      (totale, voce) =>
        totale + (voce.documenti ?? []).filter((documento) => !documento.archiviato_il).length,
      0,
    );

  const bozze = documentiDi(pubblicazioni.filter((voce) => voce.stato === "bozza"));
  const inRevisione = documentiDi(
    pubblicazioni.filter(
      (voce) => voce.stato !== "bozza" && voce.versione_di_lavoro_aperta === true,
    ),
  );
  const pubblicati = documentiDi(
    pubblicazioni.filter(
      (voce) => voce.stato === "pubblicato" && voce.versione_di_lavoro_aperta !== true,
    ),
  );

  return (
    <div className="riga">
      {bozze > 0 ? <Chip tono="attenzione">{bozze} in bozza</Chip> : null}
      {inRevisione > 0 ? <Chip tono="attenzione">{inRevisione} in revisione</Chip> : null}
      {pubblicati > 0 ? (
        <Chip tono="positivo" pallino={false}>
          {pubblicati} pubblicati
        </Chip>
      ) : null}
    </div>
  );
}

/**
 * Che cosa segnalare accanto a un documento, o `null` quando non c'e' nulla.
 *
 * Due informazioni, nelle parole del resto del back-office:
 *
 * * se il file **blocca** la pubblicazione — le pagine da scansione, l'unico
 *   caso normativo — con il tono critico, perche' ferma davvero il lavoro;
 * * il giudizio di **accessibilita'**, con lo stesso chip di ogni altra
 *   pagina. Prima qui c'erano «con avvisi» e «validazione non conclusa»: un
 *   secondo vocabolario per gli stessi fatti, e lo stesso file risultava «con
 *   avvisi» qui e «da correggere» altrove.
 *
 * Il giudizio si mostra dove c'e' qualcosa da fare (da correggere, controllo
 * non riuscito) e, per tutti, dove c'e' qualcosa da pubblicare: e' l'informazione
 * che accompagna la decisione. Su un contenuto gia' pubblico e in regola un chip
 * su ogni riga smetterebbe di essere un segnale.
 */
function segnaleDocumento(documento: Documento, inLavorazione: boolean): ReactNode {
  // Un archiviato resta nella revisione come nell'archivio della pagina: lo si
  // dice per primo, perche' pubblicando non tornera' fra i correnti.
  if (documento.archiviato_il) {
    return (
      <Chip tono="neutro" pallino={false}>
        archiviato il {documento.archiviato_il}
      </Chip>
    );
  }
  if (!documentoPubblicabile(documento)) {
    return <Chip tono="critico">pagine da scansione: blocca la pubblicazione</Chip>;
  }
  const stato = statoDi(documento);
  if (stato === "non_applicabile") return null;
  if (daCorreggere(documento) || inLavorazione) return <ChipAccessibilita stato={stato} />;
  return null;
}

/**
 * Elenco dei contenuti di un nodo, con il comando di pubblicazione.
 *
 * Il raggruppamento e' per **pubblicazione** e non per documento perche' cio'
 * che si pubblica e' la pubblicazione intera: la transizione materializza tutti
 * i documenti della versione in lavorazione. Un pulsante «Pubblica» accanto a
 * ciascun file prometterebbe una scelta per documento che il modello dati non
 * offre.
 */
export function ContenutiNodo({
  pubblicazioni,
  percorso,
  rientro = 0,
  inPubblicazione,
  onPubblica,
  onPubblicaInTutti,
}: {
  pubblicazioni: readonly Pubblicazione[];
  /** Percorso completo del nodo: fra i 219 la denominazione non e' univoca. */
  percorso: string;
  /**
   * Rientro **aggiuntivo** in rem, per allineare il blocco alla riga dell'albero
   * che lo porta. Si somma al margine di base del foglio di stile invece di
   * sostituirlo: a rientro zero il blocco resta dove il CSS lo mette.
   */
  rientro?: number;
  /** Identificativo della pubblicazione in corso di pubblicazione, se ce n'e' una. */
  inPubblicazione: number | null;
  onPubblica: (pubblicazione: Pubblicazione) => void;
  /**
   * Pubblica la pubblicazione **e** quelle delle copie collegate dei suoi
   * documenti. Assente dove la pagina non offre il gesto: il pulsante non si
   * mostra.
   */
  onPubblicaInTutti?: (pubblicazione: Pubblicazione) => void;
}) {
  if (pubblicazioni.length === 0) return null;

  return (
    <ul
      className="albero__contenuti"
      style={rientro > 0 ? { paddingLeft: `calc(1.25rem + ${rientro}rem)` } : undefined}
      aria-label={`Documenti di ${percorso}`}
    >
      {pubblicazioni.map((pubblicazione) => {
        const documenti = pubblicazione.documenti ?? [];
        const bozza = pubblicazione.stato === "bozza";
        const inAttesa = inAttesaDiPubblicazione(pubblicazione);
        const motivo = inAttesa ? motivoNonPubblicabile(pubblicazione) : null;
        const inCorso = inPubblicazione === pubblicazione.id;
        return (
          <li key={pubblicazione.id} className="albero__contenuto">
            <div className="albero__contenuto-testa">
              <div className="albero__corpo">
                <div className="albero__denominazione">{pubblicazione.titolo}</div>
                <div className="albero__meta">
                  anno {pubblicazione.anno_riferimento} ·{" "}
                  {documenti.length === 1 ? "1 documento" : `${documenti.length} documenti`}
                  {pubblicazione.data_ultimo_aggiornamento
                    ? ` · ${pubblicazione.data_ultimo_aggiornamento}`
                    : ""}
                </div>
              </div>
              <div className="riga">
                <ChipStato stato={pubblicazione.stato} />
                {/* Lo stato dice «pubblicato» anche mentre una revisione e' in
                    lavorazione: senza questo chip la riga sembrerebbe conclusa. */}
                {!bozza && pubblicazione.versione_di_lavoro_aperta ? (
                  <Chip tono="attenzione">revisione in lavorazione</Chip>
                ) : null}
                {(pubblicazione.origine ?? "interna") === "esterna" ? (
                  <Chip tono="primario">{ETICHETTA_ORIGINE.esterna}</Chip>
                ) : null}
                {inAttesa ? (
                  <Bottone
                    variante="primario"
                    dimensione="piccolo"
                    inCorso={inCorso}
                    disabled={inPubblicazione !== null || motivo !== null}
                    onClick={() => onPubblica(pubblicazione)}
                    aria-label={`${bozza ? "Pubblica" : "Pubblica la revisione di"} «${pubblicazione.titolo}» — ${percorso}`}
                  >
                    {bozza ? "Pubblica" : "Pubblica la revisione"}
                  </Bottone>
                ) : null}
                {/* Le copie dello stesso documento in altri obblighi restano in
                    bozza finche' qualcuno non le pubblica: il gesto unico evita di
                    andarle a cercare obbligo per obbligo. */}
                {inAttesa && onPubblicaInTutti && daPubblicareInTutti(pubblicazione, percorso).length > 1 ? (
                  <Bottone
                    variante="contorno"
                    dimensione="piccolo"
                    disabled={inPubblicazione !== null || motivo !== null}
                    onClick={() => onPubblicaInTutti(pubblicazione)}
                  >
                    Pubblica in tutti gli obblighi
                  </Bottone>
                ) : null}
              </div>
            </div>

            {/* Perche' il pulsante non risponde. Un comando disabilitato e muto
                lascia l'operatore a indovinare che cosa gli manca. */}
            {motivo ? <p className="campo__aiuto">{motivo}</p> : null}

            {inAttesa && !motivo ? (
              <p className="campo__aiuto">
                {validazioneNonConclusa(pubblicazione)
                  ? "Di un documento il controllo di accessibilita' non e' riuscito. La pubblicazione e' comunque consentita — nessun controllo bloccante e' stato violato — e il controllo si ripete anche dopo, con «Riverifica» in «Accessibilita' dei documenti». "
                  : ""}
                Alla pubblicazione: versione immutabile, alias «-latest» spostato, data del nodo
                ricalcolata, at_map rigenerata e rivalidata. Le versioni precedenti restano
                accessibili ai loro indirizzi.
              </p>
            ) : null}

            {documenti.length === 0 ? (
              <p className="campo__aiuto">
                Nessun documento allegato: ci sono obblighi che si assolvono con il solo contenuto
                della pagina.
              </p>
            ) : (
              <ul className="albero__documenti">
                {documenti.map((documento) => {
                  const segnale = segnaleDocumento(documento, inAttesa);
                  return (
                    <li key={documento.id} className="albero__documento">
                      <span className="albero__documento-titolo">{documento.titolo}</span>
                      <Chip tono="neutro" pallino={false}>
                        {documento.formato.toUpperCase()}
                      </Chip>
                      <span className="testo-fioco">{documento.peso}</span>
                      {documento.data_pubblicazione ? (
                        <span className="dato testo-fioco">{documento.data_pubblicazione}</span>
                      ) : null}
                      {segnale}
                      {documento.copie_collegate?.length ? (
                        <span className="campo__aiuto">
                          anche in: {testoCopie(documento.copie_collegate)}
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}
